import {
  CHILLDOC_MAX_BYTES,
  type PendingShareCopy,
  type ProjectDocumentShareState,
  type PublicProjectDocument,
  type PutShareCopyInput,
  type SetProjectDocumentShareInput,
  type ShareCopyPresign,
  type SharedFileUrls,
} from '@apm/contracts';
import { apiFetch } from '../../../lib/http';
import { fetchFileText } from '../../my-files/preview/file-content';
import { cleanChilldoc } from '../../my-files/share/share-snapshot';
import { PROJECT_FILES_SOURCE } from '../project-document-storage';

/**
 * Chia sẻ tài liệu dự án ra ngoài — APM-PDSH-b/-c. Khoá nằm dưới `['document', …]` để mọi lượt
 * làm mới tài liệu (Lưu, duyệt, thêm bản) làm mới luôn trạng thái chia sẻ — bản sạch còn thiếu
 * hiện ra ngay ở lượt đọc sau.
 */
export const documentShareKeys = {
  state: (documentId: string) => ['document', 'share', documentId] as const,
  public: (token: string, versionNo: number | null) =>
    ['public-document-share', token, versionNo] as const,
};

export const documentShareApi = {
  state: (documentId: string) =>
    apiFetch<ProjectDocumentShareState>(`/documents/${documentId}/share`),

  set: (documentId: string, input: SetProjectDocumentShareInput) =>
    apiFetch<ProjectDocumentShareState>(`/documents/${documentId}/share`, {
      method: 'PUT',
      body: input,
    }),

  revoke: (documentId: string) =>
    apiFetch<ProjectDocumentShareState>(`/documents/${documentId}/share`, { method: 'DELETE' }),

  presignCopy: (documentId: string, sizeBytes: number) =>
    apiFetch<ShareCopyPresign>(`/documents/${documentId}/share/copies/presign`, {
      method: 'POST',
      body: { sizeBytes },
    }),

  putCopy: (documentId: string, versionNo: number, input: PutShareCopyInput) =>
    apiFetch<ProjectDocumentShareState>(`/documents/${documentId}/share/copies/${versionNo}`, {
      method: 'PUT',
      body: input,
    }),
};

/** Cửa của người cầm link — `@Public` ở API, gửi kèm phiên nếu đang đăng nhập. */
export const publicDocumentShareApi = {
  open: (token: string, versionNo: number | null) =>
    apiFetch<PublicProjectDocument>(
      `/public/document-shares/${token}${versionNo === null ? '' : `?v=${versionNo}`}`,
    ),

  urls: (token: string, ids: readonly string[]) =>
    apiFetch<SharedFileUrls>(`/public/document-shares/${token}/urls`, {
      method: 'POST',
      body: { ids: [...ids] },
    }),
};

/**
 * Dựng bản sạch (không bình luận, không đề xuất chưa duyệt — `cleanChilldoc`) cho từng bản
 * `.chilldoc` còn thiếu rồi PUT thẳng S3 (luật #3). Trả trạng thái sau lượt cuối, `null` khi
 * không có gì để làm. Một bản hỏng không chặn các bản sau — ném lỗi đầu tiên ở cuối.
 */
export async function uploadShareCopies(
  documentId: string,
  pending: readonly PendingShareCopy[],
): Promise<ProjectDocumentShareState | null> {
  let state: ProjectDocumentShareState | null = null;
  let firstError: unknown = null;
  for (const item of pending) {
    try {
      const { url } = await PROJECT_FILES_SOURCE.downloadUrl(item.fileId);
      const body = new TextEncoder().encode(
        cleanChilldoc(await fetchFileText(url, CHILLDOC_MAX_BYTES)),
      );
      const presign = await documentShareApi.presignCopy(documentId, body.byteLength);
      const response = await fetch(presign.upload.uploadUrl, {
        method: 'PUT',
        headers: presign.upload.requiredHeaders,
        body,
      });
      if (!response.ok) throw new Error(`share copy upload ${response.status}`);
      state = await documentShareApi.putCopy(documentId, item.versionNo, {
        key: presign.key,
        sourceFileId: item.fileId,
      });
    } catch (error) {
      firstError ??= error;
    }
  }
  if (firstError) throw firstError;
  return state;
}
